import { useState } from "react";
import { Lightbulb, Lock, LockOpen } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import { Badge, Button, Card, cx, EmptyState, Field, NumberStepper, PageLoader, SectionTitle, Segmented, TextField, Textarea } from "@/components/ui";
import { IconPicker, Points } from "@/components/bits";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { useAction, useJobRuns, useKidJobs, useMyPitches } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { hourlyRate, JobCard, useMeaningful, useScreenLevel } from "./common";

const PITCH_ICONS = ["💡", "🧹", "🚗", "🌱", "🍳", "🐕", "🧺", "🪟", "🧰", "📚", "🎨", "🛒", "🍂", "❄️", "🧽", "👶"];

function PitchSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { family } = useSession();
  const meaningful = useMeaningful();
  const [form, setForm] = useState({
    title: "",
    description: "",
    icon: "💡",
    suggestedReward: 20,
    repeat: "once" as "once" | "anytime" | "weekly",
    estimatedMinutes: 30,
    note: "",
  });
  const pitch = useAction(
    () =>
      api.post("/jobs/pitch", {
        ...form,
        description: form.description || null,
        note: form.note || undefined,
      }),
    {
      success: "Pitch sent! A parent will review it.",
      onSuccess: () => {
        onClose();
        setForm({ ...form, title: "", description: "", note: "" });
      },
    },
  );
  const equivalent = meaningful.describe(form.suggestedReward);
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Pitch a job 💡"
      footer={
        <Button block size="lg" onClick={() => pitch.mutate()} loading={pitch.isPending} disabled={!form.title.trim() || form.suggestedReward < 1}>
          Send my pitch
        </Button>
      }
    >
      <p className="mb-4 text-sm text-muted">
        See something that needs doing? Propose it, name your price, and make your case. A parent can approve it or make a counter-offer.
      </p>
      <div className="space-y-5">
        <TextField label="What's the job?" placeholder="Wash the dog" value={form.title} maxLength={80} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Field label="Pick an icon">
          <IconPicker value={form.icon} onChange={(icon) => setForm({ ...form, icon })} choices={PITCH_ICONS} />
        </Field>
        <Field label="What exactly will you do?">
          <Textarea
            placeholder="Shampoo, rinse, dry, and clean up the bathroom after."
            value={form.description}
            maxLength={500}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Field>
        <Field label="How long will it take?">
          <NumberStepper value={form.estimatedMinutes} onChange={(v) => setForm({ ...form, estimatedMinutes: Math.max(5, v) })} min={5} max={600} step={5} suffix="min" />
        </Field>
        <Field label={`Your price (${family.currencyName})`} hint={equivalent ? `That's about ${equivalent}` : undefined}>
          <NumberStepper value={form.suggestedReward} onChange={(v) => setForm({ ...form, suggestedReward: Math.round(v) })} min={1} max={100000} step={5} />
        </Field>
        <Field label="How often?">
          <Segmented
            value={form.repeat}
            onChange={(repeat) => setForm({ ...form, repeat })}
            options={[
              { value: "once", label: "Just once" },
              { value: "weekly", label: "Weekly" },
              { value: "anytime", label: "Any time" },
            ]}
          />
        </Field>
        <Field label="Why is it worth it? (your pitch!)">
          <Textarea
            placeholder="Biscuit smells and I'll save you a trip to the groomer."
            value={form.note}
            maxLength={500}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
          <p className="mt-1 text-xs text-muted">Tip: no last names, addresses, phone numbers or school names.</p>
        </Field>
      </div>
    </Sheet>
  );
}

export function KidJobs() {
  const { family } = useSession();
  const jobs = useKidJobs();
  const pitches = useMyPitches();
  const runs = useJobRuns();
  const [pitching, setPitching] = useState(false);
  const [tab, setTab] = useState<"jobs" | "pitches" | "history">("jobs");
  const older = useScreenLevel() === "older";

  if (jobs.isLoading || !jobs.data) return <PageLoader />;
  const gate = jobs.data.gate;
  const list = jobs.data.jobs;
  const recovery = list.filter((j) => j.workItOff);
  const regular = list.filter((j) => !j.workItOff);
  const active = regular.filter((j) => j.activeRun);
  const open = regular.filter((j) => !j.activeRun && (j.available || j.locked));
  if (older) open.sort((a, b) => (hourlyRate(b) ?? -1) - (hourlyRate(a) ?? -1));
  const done = regular.filter((j) => !j.activeRun && !j.available && !j.locked);
  const pendingPitches = (pitches.data?.jobs ?? []).filter((p) => p.status === "pending").length;

  return (
    <div>
      <PageHeader
        title="Jobs"
        subtitle="Optional work. Real rewards."
        bell="/kid/notifications"
        actions={
          <Button size="sm" variant="coin" onClick={() => setPitching(true)}>
            <Lightbulb className="size-4" /> Pitch
          </Button>
        }
      />

      <div
        className={cx(
          "mt-3 flex items-center gap-3 rounded-3xl p-4",
          gate.unlocked ? "bg-brand-soft text-brand-strong" : "bg-locked-soft text-locked",
        )}
      >
        {gate.unlocked ? <LockOpen className="size-7 shrink-0" /> : <Lock className="size-7 shrink-0" />}
        <div>
          <div className="font-extrabold">{gate.unlocked ? "Jobs are unlocked" : "Jobs are locked"}</div>
          <div className="text-sm font-semibold">
            {gate.unlocked
              ? "Your responsibilities are done. Go earn!"
              : `Finish your responsibilities first — ${gate.done} of ${gate.total} done.`}
          </div>
        </div>
      </div>

      <Segmented
        className="mt-4"
        value={tab}
        onChange={setTab}
        options={[
          { value: "jobs", label: "Jobs" },
          { value: "pitches", label: <>My pitches{pendingPitches ? ` (${pendingPitches})` : ""}</> },
          { value: "history", label: "History" },
        ]}
      />

      {tab === "jobs" && (
        <>
          {recovery.length > 0 && (
            <>
              <SectionTitle>Work it off</SectionTitle>
              <p className="-mt-1 mb-2 px-1 text-sm text-muted">These help you get back to zero.</p>
              <div className="space-y-2">
                {recovery.map((j) => (
                  <JobCard key={j.id} job={j} />
                ))}
              </div>
            </>
          )}
          {active.length > 0 && (
            <>
              <SectionTitle>In progress</SectionTitle>
              <div className="space-y-2">
                {active.map((j) => (
                  <JobCard key={j.id} job={j} />
                ))}
              </div>
            </>
          )}
          <SectionTitle>{older ? "Available, best pay first" : "Available"}</SectionTitle>
          {open.length === 0 ? (
            <EmptyState icon="💡" title="No jobs open right now" action={<Button onClick={() => setPitching(true)}>Pitch your own job</Button>}>
              Entrepreneurs find work that needs doing. What could you offer?
            </EmptyState>
          ) : (
            <div className="space-y-2">
              {open.map((j) => (
                <JobCard key={j.id} job={j} />
              ))}
            </div>
          )}
          {done.length > 0 && (
            <>
              <SectionTitle>Done for now</SectionTitle>
              <div className="space-y-2 opacity-75">
                {done.map((j) => (
                  <JobCard key={j.id} job={j} compact />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {tab === "pitches" && (
        <div className="mt-4 space-y-2">
          {(pitches.data?.jobs ?? []).length === 0 ? (
            <EmptyState icon="🚀" title="No pitches yet" action={<Button onClick={() => setPitching(true)}>Pitch a job</Button>}>
              Pitch a job, name your price, and negotiate like a pro.
            </EmptyState>
          ) : (
            pitches.data!.jobs.map((p) => (
              <Card key={p.id} className="p-3">
                <div className="flex items-center gap-3">
                  <span className="flex size-12 items-center justify-center rounded-2xl bg-sunken text-2xl">{p.icon}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-extrabold">{p.title}</div>
                    <div className="text-sm text-muted">
                      You asked <Points value={p.pitchReward ?? p.reward} family={family} />
                      {p.status === "active" && p.reward !== p.pitchReward && (
                        <>
                          {" "}
                          · Counter-offer <Points value={p.reward} family={family} className="text-coin-ink" />
                        </>
                      )}
                    </div>
                  </div>
                  <Badge tone={p.status === "pending" ? "warn" : p.status === "active" ? "brand" : p.status === "rejected" ? "danger" : "neutral"}>
                    {p.status === "pending" ? "Waiting" : p.status === "active" ? "Approved" : p.status === "rejected" ? "Not now" : "Done"}
                  </Badge>
                </div>
                {p.reviewNote && <p className="mt-2 rounded-2xl bg-sunken px-3 py-2 text-sm">💬 {p.reviewNote}</p>}
              </Card>
            ))
          )}
        </div>
      )}

      {tab === "history" && (
        <div className="mt-4 space-y-2">
          {(runs.data?.runs ?? []).length === 0 ? (
            <EmptyState icon="🧾" title="No jobs yet">
              Finished jobs show up here.
            </EmptyState>
          ) : (
            runs.data!.runs.map((r) => (
              <Card key={r.id} className="flex items-center gap-3 p-3">
                <span className="text-2xl">{r.job.icon}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-bold">{r.job.title}</div>
                  <div className="text-xs text-muted">
                    {timeAgo(r.reviewedAt ?? r.submittedAt ?? r.claimedAt)}
                    {r.parentNote ? ` · “${r.parentNote}”` : ""}
                  </div>
                </div>
                {r.status === "approved" ? (
                  <Points value={r.rewardPaid ?? 0} family={family} signed className="text-brand" />
                ) : (
                  <Badge tone={r.status === "rejected" ? "danger" : r.status === "submitted" ? "warn" : "neutral"}>
                    {r.status === "rejected" ? "Redo" : r.status === "submitted" ? "Checking" : r.status === "claimed" ? "Started" : "Dropped"}
                  </Badge>
                )}
              </Card>
            ))
          )}
        </div>
      )}

      <PitchSheet open={pitching} onClose={() => setPitching(false)} />
    </div>
  );
}
